import React, { useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { API_ENDPOINTS } from "../../api/endpoints";

import FileUploader from "./components/FileUploader";
import FloatingAlert from "./components/FloatingAlert";
import ResultsTable from "./components/ResultsTable";
import ProgressModal from "./components/ProgressModal";
import FinalModal from "./components/FinalModal";
import ConfirmationModal from "./components/ConfirmationModal";
import { useAddressValidator } from "./hooks/useAddressValidator";
import { groupResults } from "./utils/GroupedResults";

const CargaManual = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const params = new URLSearchParams(location.search);
  const type = params.get("type"); // Esto será "BAJADA" si viene en la URL

  const [data, setData] = useState([]);
  const [results, setResults] = useState([]);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resetFileInput, setResetFileInput] = useState(null);

  const [progressValue, setProgressValue] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [isProgressModal, setIsProgressModal] = useState(true);
  const [modalMessage, setModalMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalState, setModalState] = useState("pregunta");
  const [message, setMessage] = useState("");

  const { fetchDrivers, fetchVehicles, normalizeAddress, searchAddresses, processResults, validateWithPlaceID } = useAddressValidator();

  const handleRedirect = () => navigate(`/panel/CrearServices/${id}?type=${type}`);

  const searchAllAddresses = async () => {
    setModalVisible(true);
    setIsProgressModal(true);
    setProgressValue(0);
    setModalMessage("Cargando...");

    const processedResults = [];
    const totalRows = data.length;

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const normalizedOrigin = normalizeAddress(row.Origen);
      const normalizedDestination = normalizeAddress(row.Destino);

      const originResults = await searchAddresses(normalizedOrigin);
      const destinationResults = await searchAddresses(normalizedDestination);

      const origin = await processResults(normalizedOrigin, originResults);
      const destination = await processResults(normalizedDestination, destinationResults, true);

      processedResults.push({ ...row, origin, destination });
      setProgressValue(Math.floor(((i + 1) / totalRows) * 100));
    }

    const finalResults = groupResults(processedResults);
    setResults(finalResults);
    setIsProgressModal(false);

    const allValid = finalResults.every((row) => row.origin?.zoneId && row.destination?.zoneId);
    setModalMessage(allValid ? "Servicios listos para cargar" : "Algunas direcciones tienen problemas. Revisa y corrige los errores antes de proceder.");
  };

  const handleSelectAlternative = async (placeID, index, isDestino = false) => {
    const validated = await validateWithPlaceID(placeID);
    if (!validated) return alert("No se pudo validar la dirección seleccionada.");

    setResults((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const alt = (isDestino ? r.destination : r.origin).alternatives.find((a) => a.placeID === placeID);
        return {
          ...r,
          [isDestino ? "Destino" : "Origen"]: alt?.address || r[isDestino ? "Destino" : "Origen"],
          [isDestino ? "destination" : "origin"]: {
            ...validated,
            address: alt?.address || validated.address,
            alternatives: [],
            isDestino,
          },
        };
      })
    );
  };

  const handleSendData = async () => {
    try {
      setModalState("cargando");

      const driversData = await fetchDrivers();
      const vehiclesData = await fetchVehicles();

      const payload = {
        id,
        data: results.map((row) => {
          const driver = driversData.find((d) => d.callsign === row.NMovil.toString() && d.active === true);
          const vehicles = vehiclesData.find((d) => d.callsign === row.NMovil.toString() && d.isSuspended === false);

          return {
            Grupo: row.Grupo,
            TipoVale: row.TipoVale,
            Fecha: row.Fecha,
            HoraInicio: row.HoraInicio,
            HoraTermino: row.HoraTermino,
            TipoMovil: row.TipoMovil,
            NMovil: row.NMovil,
            CentroCosto: row.CentroCosto,
            ValeFisico: row.ValeFisico,
            Pasajero: row.Pasajero,
            RutId: row.Rut,
            Origen: row.Origen,
            Destino: row.Destino,
            KMRecorrido: row.KMRecorrido,
            MinRecorrido: row.MinRecorrido,
            CantTAG: row.CantTAG,
            TotalPeajes: row.TotalPeajes,
            TotalEstacionamiento: row.TotalEstacionamiento,
            idConductor: driver ? driver.id : null,
            NameDriver: driver ? driver.fullName : null,
            idVehiculo: vehicles ? vehicles.id : null,
            origin: row.origin,
            destination: row.destination,
          };
        }),
      };

      const response = await fetch(API_ENDPOINTS.interna.InsertDataCarga, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Error al enviar los datos.");

      setMessage("Datos enviados exitosamente.");
      setModalState("exito");

      setTimeout(() => {
        navigate(`/panel/CrearServicios/${id}`);
      }, 1000);
    } catch (error) {
      console.error("Catch error:", error);
      setMessage(`Error: ${error.message}`);
      setModalState("error");
    }
  };

  return (
    <div className="container-fluid">
      <h2 className="text-center my-5">Carga de Servicios</h2>

      <div className="row">
        <div className="col-md-8">
          <div className="card">
            <div className="card-body mx-5 my-3">
              <div className="row">
                <div className="col-md-8">
                  <FileUploader
                    onFileRead={(data) => {
                      setErrorMessage("");
                      setFileUploaded(true);
                      setData(data);
                    }}
                    onInvalidFile={(message) => {
                      setErrorMessage(message);
                    }}
                    setResetFileInput={setResetFileInput}
                  />

                  {errorMessage && (
                    <FloatingAlert
                      message={errorMessage}
                      type="danger"
                      title="Errores en el archivo"
                      onClose={() => {
                        setErrorMessage("");
                        if (resetFileInput) resetFileInput();
                      }}
                    />
                  )}
                </div>
                <div className="col-auto">
                  <button className="btn btn-success" onClick={searchAllAddresses} disabled={!fileUploaded}>
                    Validar Direcciones
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          {results.length > 0 && (
            <div className="card">
              <div className="card-body mx-5 my-2">
                <button className="btn btn-primary btn-lg btn-pill w-100" onClick={() => setIsModalOpen(true)}>
                  Pre Carga de Servicios
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ResultsTable results={results} onSelectAlternative={handleSelectAlternative} />

      <ProgressModal visible={modalVisible && isProgressModal} progressValue={progressValue} message={modalMessage} />

      <FinalModal visible={modalVisible && !isProgressModal} message={modalMessage} onClose={() => setModalVisible(false)} onConfirm={handleSendData} />

      <ConfirmationModal
        visible={isModalOpen}
        state={modalState}
        message={message}
        onConfirm={handleSendData}
        onCancel={() => {
          if (modalState === "pregunta") setIsModalOpen(false);
          else setModalState("pregunta");
        }}
      />
    </div>
  );
};

export default CargaManual;
